import React, { useState, useRef, useEffect, useCallback } from "react";
import Icon from "@msi/cobalt-react/icon";
import { useOktaAuth } from "@okta/okta-react";
import { useBetaLabs } from '../../contexts/BetaLabsContext';
import { useDarkTheme } from '../../contexts/DarkThemeContext';
import ManageSubscriptionsModal from "../ManageSubscriptionsModal/ManageSubscriptionsModal";
import UserProfileModal from "../UserProfileModal/UserProfileModal";
import "./UserMenu.css";

const getInitials = (name: string): string => {
  if (!name) return "?";
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const UserMenu: React.FC = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isManageSubscriptionsOpen, setIsManageSubscriptionsOpen] = useState(false);
  const [isUserProfileOpen, setIsUserProfileOpen] = useState(false);
  const { isBetaLabsEnabled, toggleBetaLabs, isBetaLabsAvailable } = useBetaLabs();
  const { authState, oktaAuth } = useOktaAuth();
  const { isDarkTheme, toggleDarkTheme } = useDarkTheme();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [isSigningOut, setIsSigningOut] = useState(false);

  const userName = (authState?.idToken?.claims.name as string) || "";
  const initials = getInitials(userName);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    }
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  const toggleDropdown = () => setIsDropdownOpen((open) => !open);

  const handleBetaLabsToggle = () => {
    toggleBetaLabs();
  };

  const handleUserProfileClick = () => {
    setIsDropdownOpen(false);
    setIsUserProfileOpen(true);
  };

  const handleManageSubscriptionsClick = () => {
    setIsDropdownOpen(false);
    setIsManageSubscriptionsOpen(true);
  };

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return;
    setIsSigningOut(true);
    setIsDropdownOpen(false);
    
    // Okta destroys its session and redirects to /sign-out.  
    // The /sign-out page then redirects to PingFederate SLO (idmmaster).  
    const postLogoutUri = `${window.location.origin}/sign-out`;  

    try {  
      await oktaAuth.signOut({ postLogoutRedirectUri: postLogoutUri });  
    } catch {  
      window.location.assign(postLogoutUri);  
    } finally {  
      setIsSigningOut(false);  
    }  
  }, [isSigningOut, oktaAuth]);

  return (
    <div
      className="dropdown-trigger-wrapper user-menu-wrapper"
      style={{ position: "relative", display: "inline-block" }}
      ref={triggerRef}
    >
      <button
        className={`user-menu-trigger${isDropdownOpen ? " active" : ""}`}
        onClick={toggleDropdown}
        aria-expanded={isDropdownOpen}
        type="button"
      >
        <div className="user-menu-avatar">
          {initials}
        </div>
        <div className="user-menu-info">
          <span className="user-menu-name">{userName || "User"}</span>
          <span className="user-menu-org">Motorola Solutions Inc.</span>
        </div>
        <span className={`user-menu-arrow${isDropdownOpen ? " open" : ""}`}>&#9662;</span>
      </button>
      {isDropdownOpen && (
        <ul className="dropdown-menu show">
          <li>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center"
              onClick={handleUserProfileClick}
            >
              <Icon
                name="ic_contacts_people"
                size={20}
                color={isDarkTheme ? '#e0e0e0' : '#212529'}
                style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
              />
              User Profile
            </button>
          </li>
          <li>
            <button
              type="button"
              className="dropdown-item manage-subscriptions-link d-flex align-items-center"
              onClick={handleManageSubscriptionsClick}
            >
              <Icon
                name="ic_notification"
                size={18}
                color={isDarkTheme ? '#e0e0e0' : '#212529'}
                style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
              />
              Manage Subscription
            </button>
          </li>
          {isBetaLabsAvailable && (
            <>
              <li>
                <div
                  className="dropdown-item d-flex align-items-center justify-content-between"
                  style={{ cursor: "pointer" }}
                  onClick={handleBetaLabsToggle}
                >
                  <div className="d-flex align-items-center">
                    <span
                      className="me-2"
                      style={{
                        fontSize: '15px',
                        color: isBetaLabsEnabled ? '#28a745' : '#6c757d'
                      }}
                    >
                      🧪
                    </span>
                    <span style={{ color: isBetaLabsEnabled ? '#28a745' : (isDarkTheme ? '#e0e0e0' : '#212529') }}>
                      Enable Beta Features
                    </span>
                  </div>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isBetaLabsEnabled}
                      onChange={() => { }} // Handled by onClick on parent
                      style={{ pointerEvents: 'none' }}
                    />
                  </div>
                </div>
              </li>
            </>
          )}
          <li>
            <div
              className="dropdown-item d-flex align-items-center justify-content-between"
              style={{ cursor: "pointer" }}
              onClick={toggleDarkTheme}
            >
              <div className="d-flex align-items-center">
                <Icon
                  name='ic_light_on'
                  size={18}
                  color={isDarkTheme ? '#ffffff' : '#000000'}
                  style={{ width: 18, height: 18, marginRight: 8, flexShrink: 0 }}
                />
                <span style={{ color: isDarkTheme ? '#ffffff' : undefined }}>
                  {isDarkTheme ? 'Light Theme' : 'Dark Theme'}
                </span>
              </div>
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={isDarkTheme}
                  readOnly
                  aria-label="Toggle dark theme"
                  style={{ pointerEvents: 'none' }}
                />
              </div>
            </div>
          </li>
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center"
              onClick={handleSignOut}
              disabled={isSigningOut}
            >
              {isSigningOut ? 'Signing Out...' : 'Sign Out'}
            </button>
          </li>
        </ul>
      )}
      <ManageSubscriptionsModal
        isOpen={isManageSubscriptionsOpen}
        onClose={() => setIsManageSubscriptionsOpen(false)}
      />
      <UserProfileModal
        isOpen={isUserProfileOpen}
        onClose={() => setIsUserProfileOpen(false)}
      />
    </div>
  );
};

export default UserMenu;
